import { useState } from "react";
import { ArrowLeft, ChevronRight, Lock, TriangleAlert } from "lucide-react";
import { Button } from "~/components/ui/button";
import { ChipsMultiSelect } from "~/components/ui/chips-multi-select";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { Skeleton } from "~/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { Textarea } from "~/components/ui/textarea";
import useUser from "~/contexts/user";
import { cn } from "~/lib/utils";
import { ResourceLoadError } from "~/pages/manifold-nextgen/components/ResourceLoadError";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { evalCasePath, EVAL_SCORE_MAX, type EvalCase, type EvalCaseUpdate } from "~/services/evals";
import { apiMutate } from "~/utils/apiClient";
import { CaseResultView } from "./CaseResultDrawer";
import { EvaluatorKind, LATENCY_EVALUATOR } from "./constants/evaluators";
import { OutcomeBadge } from "./primitives/badges";
import { DatasetNames, type DatasetRefs } from "./primitives/chips";
import { ScoreValue } from "./primitives/figures";
import { formatDuration, formatEvalTimestamp } from "./primitives/format";
import { CaseQuery, JsonBlock, Section } from "./primitives/layout";
import {
  type CaseHealthCell,
  type CaseHealthRow,
  evaluatorsForCase,
  splitCaseInputs,
} from "./selectors/cases";
import { parseApiTimestamp } from "./selectors/core";
import { useEvalCase } from "./useEvalApi";

const CaseTab = {
  CONFIG: "config",
  RUNS: "runs",
} as const;
type CaseTab = (typeof CaseTab)[keyof typeof CaseTab];

interface CaseDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string | null;
  /**
   * This case's own run history, as the cases table reads it. Null when the
   * case is not in the table's rows, which only leaves the history tabs empty.
   */
  history: CaseHealthRow | null;
  /**
   * A run to open on, as when a cell of the cases table is clicked. The drawer
   * then starts on the Runs tab with that run showing; the other tabs stay one
   * click away. Unknown to the case's history, it is ignored.
   */
  initialRunId?: string | null;
  /** Catalog datasets by id, so the case names its datasets rather than listing uuids. */
  datasets: DatasetRefs;
  /** Tags already in use, offered so editing a case's tags can join an existing group. */
  availableTags: string[];
  onSaved: () => void;
}

/**
 * Everything about one case in one drawer: its definition, its health over time
 * and the runs behind it. These are facts about the same case, not modes of it,
 * so they are tabs of one panel rather than separate views of the table.
 *
 * `inputs` and `expectations` stay read-only: they are the scoring definition,
 * editing them bumps `version` and makes every past result incomparable, and they
 * are round-tripped from the YAML suites a JSONB editor here would diverge from.
 */
export const CaseDetailSheet = ({
  open,
  onOpenChange,
  caseId,
  history,
  initialRunId,
  datasets,
  availableTags,
  onSaved,
}: CaseDetailSheetProps) => {
  const { data: evalCase, error, isLoading, mutate } = useEvalCase(caseId);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 sm:max-w-xl">
        <SheetHeader className="pr-12">
          {/* Before the case loads there is nothing to title, so the dialog keeps an
              accessible name without painting a placeholder over the loader. */}
          <SheetTitle className={cn("font-mono text-sm break-all", !evalCase && "sr-only")}>
            {evalCase?.name ?? "Eval case"}
          </SheetTitle>
          {isLoading && !evalCase && <Skeleton className="h-5 w-48" />}
          <SheetDescription asChild>
            {evalCase ? (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                <DatasetNames ids={evalCase.dataset_ids} datasets={datasets} className="text-xs" />
                <span aria-hidden>·</span>
                {/* The version is what a result's `case_version` has to match
                    to be comparable, so it is stated rather than implied. */}
                <span className="tabular-nums">v{evalCase.version}</span>
                <span aria-hidden>·</span>
                <span className="truncate">{evalCase.created_by ?? "Imported, no author"}</span>
              </div>
            ) : isLoading ? (
              <Skeleton className="h-4 w-64" />
            ) : (
              <span className="sr-only">The case definition.</span>
            )}
          </SheetDescription>
        </SheetHeader>

        {isLoading && !evalCase ? (
          <div className="flex flex-col gap-3 p-4" role="status" aria-label="Loading eval case">
            <Skeleton className="h-16 rounded-lg" />
            <Skeleton className="h-40 rounded-lg" />
          </div>
        ) : error || !evalCase ? (
          <ResourceLoadError error={error} resourceLabel="eval case" className="py-16" />
        ) : (
          // Keyed by case so one case's unsaved edits and open run never carry
          // over to the next one opened in the same drawer.
          <CaseDrawerBody
            key={evalCase.id}
            evalCase={evalCase}
            history={history}
            initialRunId={initialRunId ?? null}
            availableTags={availableTags}
            onSaved={async () => {
              // The version may have moved, and the form compares against it.
              await mutate();
              onSaved();
            }}
          />
        )}
      </SheetContent>
    </Sheet>
  );
};

/** Which run the Runs tab has open, and which of its iterations. */
interface OpenRun {
  runId: string;
  resultId: string;
}

const CaseDrawerBody = ({
  evalCase,
  history,
  initialRunId,
  availableTags,
  onSaved,
}: {
  evalCase: EvalCase;
  history: CaseHealthRow | null;
  initialRunId: string | null;
  availableTags: string[];
  onSaved: () => void | Promise<void>;
}) => {
  const user = useUser();
  const cells = history?.cells ?? [];
  // Read once, when the drawer opens: after that the reviewer's own tab and run
  // choices are the state, not the click that brought them here.
  const [initialCell] = useState(() => cells.find((cell) => cell.runId === initialRunId) ?? null);
  const [tab, setTab] = useState<CaseTab>(initialCell ? CaseTab.RUNS : CaseTab.CONFIG);
  const [openRun, setOpenRun] = useState<OpenRun | null>(
    initialCell ? { runId: initialCell.runId, resultId: initialCell.results[0].id } : null
  );

  // Only the case's author may PATCH it — the API answers 403 to anyone else,
  // and a case with no recorded author has no creator to match, so it is not
  // editable at all. Reflecting that here means the fields are disabled rather
  // than offering an edit that fails on save.
  const canEdit =
    !!evalCase.created_by &&
    !!user?.email &&
    evalCase.created_by.toLowerCase() === user.email.toLowerCase();

  const openCell = openRun ? (cells.find((cell) => cell.runId === openRun.runId) ?? null) : null;

  const showRun = (cell: CaseHealthCell) => {
    setOpenRun({ runId: cell.runId, resultId: cell.results[0].id });
    setTab(CaseTab.RUNS);
  };

  return (
    <Tabs
      value={tab}
      onValueChange={(next) => setTab(next as CaseTab)}
      className="min-h-0 flex-1 gap-0"
    >
      <div className="mx-4 mt-2">
        <TabsList>
          <TabsTrigger value={CaseTab.CONFIG}>Config</TabsTrigger>
          <TabsTrigger value={CaseTab.RUNS}>Runs</TabsTrigger>
        </TabsList>
      </div>

      {/* Kept mounted while another tab is open, so edits not yet saved survive
          a look at the history. */}
      <TabsContent
        value={CaseTab.CONFIG}
        forceMount
        className="min-h-0 flex-1 flex-col data-[state=active]:flex data-[state=inactive]:hidden"
      >
        <CaseConfigForm
          evalCase={evalCase}
          availableTags={availableTags}
          canEdit={canEdit}
          onSaved={onSaved}
        />
      </TabsContent>

      <TabsContent value={CaseTab.RUNS} className="min-h-0 flex-1 overflow-y-auto p-4">
        {openRun && openCell ? (
          <div className="flex flex-col gap-4">
            <button
              type="button"
              onClick={() => setOpenRun(null)}
              className="flex w-fit cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-3" aria-hidden />
              All Runs
            </button>
            <CaseResultView
              startedAt={
                openCell.run
                  ? formatEvalTimestamp(parseApiTimestamp(openCell.run.started_at))
                  : undefined
              }
              runId={openRun.runId}
              resultId={openRun.resultId}
              caseId={evalCase.id}
              siblingResultIds={openCell.results.map((result) => result.id)}
              onSelectResult={(resultId) =>
                setOpenRun((current) => (current ? { ...current, resultId } : current))
              }
            />
          </div>
        ) : (
          <RunList cells={cells} onSelectCell={showRun} />
        )}
      </TabsContent>
    </Tabs>
  );
};

// Shared by the header and the rows so the columns line up.
const RUN_LIST_STARTED_CLS = "w-32 shrink-0";
const RUN_LIST_PASSED_CLS = "w-36 shrink-0";
const RUN_LIST_RESULT_CLS = "w-20 shrink-0";

/** Every run of the case, newest first. */
const RunList = ({
  cells,
  onSelectCell,
}: {
  cells: CaseHealthCell[];
  onSelectCell: (cell: CaseHealthCell) => void;
}) => {
  if (cells.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No readable run has exercised this case yet.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {/* Hidden from assistive tech: each row's button already reads out its own fields. */}
      <div
        aria-hidden
        className="mx-px flex items-center gap-3 px-3 text-xs font-medium text-muted-foreground"
      >
        <span className={RUN_LIST_STARTED_CLS}>Started</span>
        <span className={RUN_LIST_PASSED_CLS}>Iterations passed</span>
        <span className={RUN_LIST_RESULT_CLS}>Result</span>
        <span>Score</span>
      </div>
      <ul
        aria-label="Runs of this case"
        className="divide-y divide-border overflow-hidden rounded-md border border-border"
      >
        {[...cells].reverse().map((cell) => (
          <li key={cell.runId}>
            <button
              type="button"
              onClick={() => onSelectCell(cell)}
              className="flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
            >
              <span
                className={cn(
                  RUN_LIST_STARTED_CLS,
                  "text-xs font-medium whitespace-nowrap text-foreground"
                )}
              >
                {cell.run ? formatEvalTimestamp(parseApiTimestamp(cell.run.started_at)) : ""}
              </span>
              <span className={cn(RUN_LIST_PASSED_CLS, "text-xs text-muted-foreground")}>
                {cell.passedCount} of {cell.judgedCount}{" "}
                {cell.judgedCount === 1 ? "iteration" : "iterations"} passed
              </span>
              <span className={RUN_LIST_RESULT_CLS}>
                <OutcomeBadge outcome={cell.outcome} />
              </span>
              <ScoreValue score={cell.meanScore} className="text-xs" />
              <ChevronRight className="ml-auto size-4 shrink-0 text-muted-foreground" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * The case's definition: its metadata and thresholds as fields that are always
 * live, then the read-only scoring definition they sit beside. Disabled for
 * anyone but the author, who also sees no Save.
 */
const CaseConfigForm = ({
  evalCase,
  availableTags,
  canEdit,
  onSaved,
}: {
  evalCase: EvalCase;
  availableTags: string[];
  canEdit: boolean;
  onSaved: () => void | Promise<void>;
}) => {
  const savedName = evalCase.name;
  const savedDescription = evalCase.description ?? "";
  const savedTags = evalCase.tags;
  const savedThreshold = evalCase.pass_threshold == null ? "" : String(evalCase.pass_threshold);
  const savedMaxLatency =
    evalCase.max_latency_seconds == null ? "" : String(evalCase.max_latency_seconds);

  const [name, setName] = useState(savedName);
  const [description, setDescription] = useState(savedDescription);
  const [tags, setTags] = useState(savedTags);
  const [threshold, setThreshold] = useState(savedThreshold);
  const [maxLatency, setMaxLatency] = useState(savedMaxLatency);
  const { query, rest } = splitCaseInputs(evalCase.inputs);

  const isDirty =
    name !== savedName ||
    description !== savedDescription ||
    tags.length !== savedTags.length ||
    tags.some((tag, index) => tag !== savedTags[index]) ||
    threshold !== savedThreshold ||
    maxLatency !== savedMaxLatency;

  const discardEdits = () => {
    setName(savedName);
    setDescription(savedDescription);
    setTags(savedTags);
    setThreshold(savedThreshold);
    setMaxLatency(savedMaxLatency);
  };

  const parsedThreshold = threshold.trim() === "" ? null : Number(threshold);
  const parsedLatency = maxLatency.trim() === "" ? null : Number(maxLatency);
  const isThresholdValid = parsedThreshold == null || Number.isFinite(parsedThreshold);
  const isLatencyValid =
    parsedLatency == null || (Number.isInteger(parsedLatency) && parsedLatency > 0);
  const isNameValid = name.trim().length > 0;

  // Editing either threshold bumps the case's `version`, so results before and
  // after are no longer apples-to-apples. Saying so at the moment of the edit is
  // the only place a reviewer can act on it.
  const changesScoring =
    parsedThreshold !== evalCase.pass_threshold || parsedLatency !== evalCase.max_latency_seconds;

  const { execute, isPending } = useAsyncMutation(
    async () => {
      const body: EvalCaseUpdate = {
        name: name.trim(),
        description: description.trim() || null,
        tags,
        pass_threshold: parsedThreshold,
        max_latency_seconds: parsedLatency,
      };
      return apiMutate<EvalCase>(evalCasePath(evalCase.id), "PATCH", body);
    },
    { errorMessage: "Could not save the case.", onSuccess: onSaved }
  );

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-6">
          <fieldset disabled={!canEdit || isPending} className="flex flex-col gap-5">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="eval-case-name">Name</Label>
              <Input
                id="eval-case-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="font-mono text-xs"
                aria-invalid={!isNameValid || undefined}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="eval-case-description">Description</Label>
              <Textarea
                id="eval-case-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={3}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <ChipsMultiSelect
                label="Tags"
                selectedValues={tags}
                options={availableTags.map((tag) => ({ value: tag, label: tag }))}
                onChange={setTags}
                allowCreate
              />
              <p className="text-xs text-muted-foreground">
                Runs select cases by tag, so these decide which runs cover this case.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="eval-case-threshold">Pass threshold</Label>
                <Input
                  id="eval-case-threshold"
                  type="number"
                  step="0.1"
                  value={threshold}
                  onChange={(event) => setThreshold(event.target.value)}
                  placeholder="None"
                  aria-invalid={!isThresholdValid || undefined}
                />
                <p className="text-xs text-muted-foreground">
                  Blank means assertions alone decide pass or fail.
                </p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="eval-case-latency">Latency limit (s)</Label>
                <Input
                  id="eval-case-latency"
                  type="number"
                  min={1}
                  value={maxLatency}
                  onChange={(event) => setMaxLatency(event.target.value)}
                  placeholder="None"
                  aria-invalid={!isLatencyValid || undefined}
                />
                <p className="text-xs text-muted-foreground">Blank means the case is not timed.</p>
              </div>
            </div>
          </fieldset>

          {canEdit && changesScoring && (
            <p className="rounded-md border border-orange-300 bg-orange-50 p-3 text-xs text-orange-800">
              This changes how the case is scored, so saving bumps it to version{" "}
              {evalCase.version + 1}. Results recorded against version {evalCase.version} stay
              comparable only with each other.
            </p>
          )}

          <Section title="Question">
            {query ? (
              <CaseQuery query={query} />
            ) : (
              <p className="text-sm text-muted-foreground">
                No question field — the inputs below are all this case sends.
              </p>
            )}
            {/* Anything the suite put in `inputs` beyond the question. Usually
                empty, so it costs nothing when there is nothing to say. */}
            {Object.keys(rest).length > 0 && <JsonBlock value={rest} />}
          </Section>

          <Section
            title="Evaluators"
            subtitle="The runner builds these from the expectations below; the case never names them directly."
          >
            <EvaluatorList evalCase={evalCase} />
          </Section>

          <Section
            title="Expectations"
            subtitle="The raw blob, including any key the runner ignores."
          >
            <JsonBlock value={evalCase.expectations} />
          </Section>
        </div>
      </div>

      {canEdit ? (
        <SheetFooter className="flex-row items-center justify-end border-t border-border">
          {isDirty && (
            <Button variant="outline" onClick={discardEdits} disabled={isPending}>
              Cancel
            </Button>
          )}
          <Button
            onClick={() => void execute()}
            disabled={!isDirty || isPending || !isNameValid || !isThresholdValid || !isLatencyValid}
          >
            {isPending ? "Saving…" : "Save"}
          </Button>
        </SheetFooter>
      ) : (
        <SheetFooter className="border-t border-border">
          <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="size-3.5" aria-hidden />
            Only the case&apos;s author can edit it
          </p>
        </SheetFooter>
      )}
    </>
  );
};

/**
 * The evaluators this case resolves to, and what each does to the result. A
 * judge's score is what `score` averages; an assertion only flips `passed`. So a
 * case of assertions records no score and one of judges can fail only on its
 * threshold — both easy to create by accident, neither visible in the raw blob.
 */
/**
 * What a case is judged by, and what turns those judgements into a verdict.
 * Exported for its own tests: the verdict copy states the runner's rule, which is
 * not derivable from anything on the case.
 */
export const EvaluatorList = ({ evalCase }: { evalCase: EvalCase }) => {
  const evaluators = evaluatorsForCase(evalCase.expectations, evalCase.max_latency_seconds);
  const threshold = evalCase.pass_threshold;

  if (evaluators.length === 0) {
    return (
      <p className="flex items-start gap-1.5 rounded-md border border-orange-300 bg-orange-50 px-3 py-2 text-sm text-orange-800">
        <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        No evaluator applies to this case. It will run, spend agent time and record no score or
        outcome.
      </p>
    );
  }

  const hasJudge = evaluators.some((entry) => entry.kind === EvaluatorKind.JUDGE);
  const hasAssertion = evaluators.some((entry) => entry.kind === EvaluatorKind.ASSERTION);

  return (
    <div className="flex flex-col gap-2">
      <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
        {evaluators.map((entry) => (
          <li key={entry.name} className="flex items-baseline justify-between gap-3 px-3 py-2">
            {/* The class name on hover: it is what the result's
                `evaluator_scores` is keyed by and what a reader greps for. */}
            <span className="truncate text-sm text-foreground" title={entry.name}>
              {entry.label}
            </span>
            {/* The latency assertion states its own bound rather than "pass or
                fail", which is the whole content of that row: every other
                evaluator's threshold lives in the expectations blob, and this
                one is a column on the case. */}
            <span className="shrink-0 text-xs text-muted-foreground">
              {entry.name === LATENCY_EVALUATOR && evalCase.max_latency_seconds != null
                ? `under ${formatDuration(evalCase.max_latency_seconds)}`
                : entry.kind === EvaluatorKind.JUDGE
                  ? `scores 1-${EVAL_SCORE_MAX}`
                  : "pass or fail"}
            </span>
          </li>
        ))}
      </ul>
      {/* What turns the rows above into an outcome. Not an evaluator, so it
          reads as the step after them rather than as another list item — and
          not a fact for the strip at the top of the panel either, since it is
          meaningless without the judges it applies to. */}
      {threshold != null ? (
        <p className="text-xs text-muted-foreground">
          {hasJudge ? (
            <>
              Passes when <span className="font-medium text-foreground">every</span> judge score
              reaches{" "}
              <span className="font-medium text-foreground tabular-nums">
                {threshold} / {EVAL_SCORE_MAX}
              </span>
              . The score above it is the mean, which can clear the bar when a single judge did not.
            </>
          ) : (
            <>
              Passes at {threshold} / {EVAL_SCORE_MAX}, but nothing here scores the answer, so the
              threshold never applies.
            </>
          )}
        </p>
      ) : (
        hasAssertion && (
          <p className="text-xs text-muted-foreground">
            No pass threshold, so the assertions alone decide pass or fail.
          </p>
        )
      )}
      {!hasJudge && (
        <p className="text-xs text-muted-foreground">
          No evaluator scores the answer, so this case records pass or fail and no score.
        </p>
      )}
      {!hasAssertion && threshold != null && (
        <p className="text-xs text-muted-foreground">
          No evaluator can fail this case on its own, so the threshold above is what decides it.
        </p>
      )}
      {!hasAssertion && threshold == null && (
        <p className="flex items-start gap-1.5 text-xs text-orange-800">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          Nothing here can fail this case, so it fails only when a judge errors or the run supplies
          its own threshold.
        </p>
      )}
    </div>
  );
};
